import { BadRequest, NotAuthenticated, NotAuthorized, NotFound, SessionExpired } from "../layers/ErrorBoundary"

export function parseDates(item) {
    if (item instanceof Array) return item.map(parseDates)
    if (item instanceof Object) return Object.fromEntries(Object.entries(item).map(([key, value]) => [key, parseDates(value)]))
    if (!isNaN(new Date(item)) && new Date(item).toISOString() === item) {
        let newItem = new Date(item)
        newItem.toString = newItem.toISOString
        return newItem
    }
    return item
}

const isFile = x => x instanceof File || (x instanceof Array && x.some(item => item instanceof File))
const hasFile = body => body ? Object.values(body).some(isFile) : false

function encodeBody(body) {
    if (!body) return body
    if (hasFile(body)) {
        const formData = new FormData()
        const jsonBody = {}
        for (let [key, value] of Object.entries(body)) {
            if (value instanceof File) formData.append(key, value)
            else if (isFile(value)) {
                for (const file of value) formData.append(key, file)
            }
        }
        for (let key in body) if (!isFile(body[key])) jsonBody[key] = body[key]
        formData.append('jsonBody', JSON.stringify(jsonBody))
        return formData
    } else return JSON.stringify(body)
}

const handleResponse = async response => {
    if (response.status === 401) throw new NotAuthenticated(`${response.status}: ${response.url}`)
    if (response.status === 403) throw new NotAuthorized(`${response.status}: ${response.url}`)
    if (response.status === 404) throw new NotFound(`${response.status}: ${response.url}`)
    if (response.status === 498) throw new SessionExpired()
    const contentType = response.headers.get('Content-Type') || ''
    const answer =
        contentType.includes('application/json') ? parseDates(await response.json())
            : contentType.includes('text/plain') ? await response.text()
                : await response.blob()

    const error = answer?.error ? answer.error?.join?.(', ') || answer.error
        : contentType.includes('text/plain') ? answer : undefined
    if (response.status === 400) throw new BadRequest(error)
    if (!response.ok) throw new Error(error)

    return answer
}

export function useApi() {
    return {
        get: async (path) => {
            return await fetch(process.env.REACT_APP_API_URL + path, {
                credentials: 'include'
            }).then(handleResponse)
        },
        post: async (path, body) => {
            return await fetch(process.env.REACT_APP_API_URL + path, {
                credentials: 'include',
                method: 'POST',
                body: encodeBody(body),
                headers: body && !hasFile(body) ? { "Content-Type": "application/json" } : {}
            }).then(handleResponse)
        },
        patch: async (path, body) => {
            return await fetch(process.env.REACT_APP_API_URL + path, {
                credentials: 'include',
                method: 'PATCH',
                body: encodeBody(body),
                headers: body && !hasFile(body) ? { "Content-Type": "application/json" } : {}
            }).then(handleResponse)
        },
        delete: async (path) => {
            return await fetch(process.env.REACT_APP_API_URL + path, {
                credentials: 'include',
                method: 'DELETE'
            }).then(handleResponse)
        }
    }
}